import { AppstoreOutlined, SearchOutlined } from "@ant-design/icons";
import { Input, Table } from "antd";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { CREATE_jwel } from "../Config/Config";
import PdfExcelPrint from "../Utilities/PdfExcelPrint";
import styles from "../shared/Inventory.module.css";
import InventoryPageHeader from "../shared/InventoryPageHeader";
import InventoryTable from "../shared/InventoryTable";
import Header from "../Header";
import SidebarDrawer from "../SidebarDrawer";

/* ---------------- Shared card-view field maps (mobile) ---------------- */
const primaryFields = [
  { key: "ProdName", label: "Product" },
  { key: "PTYPE", label: "Purchase Type" },
  { key: "QTY", label: "Qty", badge: "pieces" },
];
const secondaryFields = [
  { key: "AMOUNT", label: "Amount" },
  { key: "SAMOUNT", label: "SAmount" },
];

const ProductWiseStock = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchType, setSearchType] = useState("");
  const [searchProduct, setSearchProduct] = useState("");
  const [tableVisible, setTableVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const tenantName = localStorage.getItem("tenantName");
  const [open, setOpen] = useState(false);
  const userArea = localStorage.getItem("city");
  const userName = localStorage.getItem("user");
  const singleImage = localStorage.getItem("singleImage");

  const toggleDrawer = () => {
    setOpen(false);
  };

  /* ── fetch all product wise stock from CURRENT_STOCK table ── */
  useEffect(() => {
    setLoading(true);
    setTableVisible(false);

    axios
      .get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=CURRENT_STOCK`,
        {
          headers: {
            tenantName: tenantName,
          },
        },
      )
      .then((response) => {
        // filter items whose Qty is > 0
        const detailsData = (response.data || []).filter(
          (item) => Number(item?.Qty ?? item?.QTY ?? 0) > 0,
        );

        // Group by Purchase Type + Product Name
        const map = new Map();

        detailsData.forEach((item) => {
          const ptype = item.PTYPE || "UNKNOWN";
          const prodName =
            item.ProdName || item.PRODNAME || item.ItemName || "UNKNOWN";
          const key = `${ptype}_${prodName}`;

          if (!map.has(key)) {
            map.set(key, {
              PTYPE: ptype,
              ProdName: prodName,
              QTY: 0,
              AMOUNT: 0,
              SAMOUNT: 0,
            });
          }

          const entry = map.get(key);
          const qty = Number(item.Qty ?? item.QTY ?? 0);
          const dPrice = Number(item.DPrice ?? item.DPRICE ?? item.dPrice ?? 0);
          const cPrice = Number(item.CPrice ?? item.CPRICE ?? item.cPrice ?? 0);

          entry.QTY += qty;
          entry.AMOUNT += qty * dPrice;
          entry.SAMOUNT += qty * cPrice;
        });

        const calculatedData = Array.from(map.values()).map((item, index) => ({
          ...item,
          key: index + 1,
          serialNo: index + 1,
        }));

        setData(calculatedData);
        setTimeout(() => setTableVisible(true), 80);
      })
      .catch((error) => {
        console.error("Error fetching product wise stock:", error);
        setData([]);
        setTimeout(() => setTableVisible(true), 80);
      })
      .finally(() => setLoading(false));
  }, [tenantName]);

  useEffect(() => {
    let filtered = [...data];

    if (searchType) {
      const term = searchType.toLowerCase();
      filtered = filtered.filter((item) =>
        (item.PTYPE || "").toLowerCase().includes(term),
      );
    }

    if (searchProduct) {
      const term = searchProduct.toLowerCase();
      filtered = filtered.filter((item) =>
        (item.ProdName || "").toLowerCase().includes(term),
      );
    }

    setFilteredData(
      filtered.map((item, index) => ({
        ...item,
        serialNo: index + 1,
      })),
    );
  }, [data, searchType, searchProduct]);

  const columns = [
    {
      title: "S.No",
      dataIndex: "serialNo",
      width: 50,
      align: "center",
      key: "serialNo",
      className: "col-sno",
    },
    {
      title: "Purchase Type",
      dataIndex: "PTYPE",
      key: "PTYPE",
      align: "left",
    },
    {
      title: "Product Name",
      dataIndex: "ProdName",
      key: "ProdName",
      align: "left",
      className: "col-productname",
      render: (value) => <span className="tagBadge">{value}</span>,
    },
    {
      title: "QTY",
      dataIndex: "QTY",
      key: "QTY",
      align: "center",
      className: "col-pieces",
      render: (value) => <span className="piecesBadge">{value}</span>,
    },
    {
      title: "Amount",
      dataIndex: "AMOUNT",
      key: "AMOUNT",
      align: "right",
      render: (value) => <span>{Number(value || 0).toFixed(2)}</span>,
    },
    {
      title: "SAmount",
      dataIndex: "SAMOUNT",
      key: "SAMOUNT",
      align: "right",
      className: "col-netwt",
      render: (value) => (
        <span className="wtBadge">{Number(value || 0).toFixed(2)}</span>
      ),
    },
  ];

  const getTotals = (data) => {
    return data.reduce(
      (acc, item) => {
        acc.QTY += Number(item.QTY || 0);
        acc.AMOUNT += Number(item.AMOUNT || 0);
        acc.SAMOUNT += Number(item.SAMOUNT || 0);
        return acc;
      },
      { QTY: 0, AMOUNT: 0, SAMOUNT: 0 },
    );
  };

  const formattedData = useMemo(
    () =>
      filteredData.map((item, index) => ({
        ...item,
        serialNo: index + 1,
        AMOUNT: Number(item.AMOUNT || 0),
        SAMOUNT: Number(item.SAMOUNT || 0),
      })),
    [filteredData],
  );

  const totals = useMemo(() => getTotals(filteredData), [filteredData]);

  const exportData = [
    ...formattedData,
    {
      serialNo: "Total",
      PTYPE: "",
      ProdName: "",
      QTY: totals.QTY,
      AMOUNT: totals.AMOUNT.toFixed(2),
      SAMOUNT: totals.SAMOUNT.toFixed(2),
    },
  ];

  return (
    <div className={styles.page}>
      <Header setOpen={setOpen} />
      <InventoryPageHeader
        title="Product Wise Stock"
        subtitle="Inventory · Product Wise Stock"
        icon={<AppstoreOutlined style={{ color: "#fff", fontSize: 16 }} />}
        recordCount={filteredData.length}
        visible={filteredData.length > 0}
        stats={[
          { label: "Records", value: filteredData.length, color: "blue" },
          { label: "Qty", value: totals.QTY, color: "orange" },
          {
            label: "Amount",
            value: totals.AMOUNT.toFixed(2),
            color: "amber",
          },
          {
            label: "SAmount",
            value: totals.SAMOUNT.toFixed(2),
            color: "green",
          },
        ]}
      />

      <div className={styles.toolbar} style={{ flexWrap: "wrap", rowGap: 12 }}>
        <div
          className={styles.toolbarLeft}
          style={{ flexWrap: "wrap", rowGap: 12 }}
        >
          <div className="mainProductHighlight">
            <span className="mainProductIcon">
              <SearchOutlined />
            </span>
            <div className="mainProductInner">
              <div className={styles.toolbarLabel}>Purchase Type</div>
              <Input
                placeholder="Search Purchase Type"
                className="mainProductPicker"
                style={{ width: "12rem" }}
                allowClear
                suffix={<SearchOutlined style={{ color: "#1677ff" }} />}
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
              />
            </div>
          </div>

          <div className="mainProductHighlight">
            <span className="mainProductIcon">
              <SearchOutlined />
            </span>
            <div className="mainProductInner">
              <div className={styles.toolbarLabel}>Product Name</div>
              <Input
                placeholder="Search Product Name"
                className="mainProductPicker"
                style={{ width: "12rem" }}
                allowClear
                suffix={<SearchOutlined style={{ color: "#1677ff" }} />}
                value={searchProduct}
                onChange={(e) => setSearchProduct(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className={styles.toolbarRight}>
          <PdfExcelPrint
            data={exportData}
            columns={columns}
            fileName="ProductWiseStock"
            disabled={formattedData.length === 0}
          />
        </div>
      </div>

      <InventoryTable
        columns={columns}
        dataSource={formattedData}
        rowKey="serialNo"
        tableClassName="mr-table"
        tableEnter={tableVisible}
        loading={loading}
        emptyText="No product wise stock records found for the selected filters"
        primaryFields={primaryFields}
        secondaryFields={secondaryFields}
        summary={() => (
          <Table.Summary.Row style={{ background: "#f5f5f5", fontWeight: 600 }}>
            <Table.Summary.Cell>Total</Table.Summary.Cell>
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell align="center">{totals.QTY}</Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.AMOUNT.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.SAMOUNT.toFixed(2)}
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />

      <style>{`
  @keyframes mainProductPulse {
    0%   { box-shadow: 0 0 0 0 rgba(24,144,255,0.30); }
    70%  { box-shadow: 0 0 0 8px rgba(24,144,255,0); }
    100% { box-shadow: 0 0 0 0 rgba(24,144,255,0); }
  }
  .mainProductHighlight {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 18px 8px 10px;
    border-radius: 14px;
    background: #eef6ff;
    border: 1.5px solid #65aefd;
    box-shadow: 0 0 0 1.5px #bcdcff inset;
    animation: mainProductPulse 2.4s infinite;
    min-width: 210px;
    flex-shrink: 0;
  }
  .mainProductIcon {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(24,144,255,0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #1677ff;
    flex-shrink: 0;
  }
  .mainProductInner {
    display: flex;
    flex-direction: column;
    gap: 3px;
    flex: 1;
    min-width: 0;
  }

  /* ── Search inputs ── */
  .mainProductPicker.ant-input-affix-wrapper,
  .mainProductPicker.ant-input {
    height: 34px !important;
    border-radius: 8px !important;
    background: #ffffff !important;
    border: 1px solid #d6e8ff !important;
    width: 100%;
  }
  .mainProductPicker.ant-input-affix-wrapper:hover,
  .mainProductPicker.ant-input:hover {
    border-color: #65aefd !important;
  }
  .mainProductPicker.ant-input-affix-wrapper-focused,
  .mainProductPicker.ant-input:focus {
    border-color: #1890ff !important;
    box-shadow: none !important;
  }
  .mainProductPicker input.ant-input {
    font-size: 13px;
    font-weight: 600;
    color: #1f2430;
    background: transparent !important;
  }
  .mainProductPicker input.ant-input::placeholder {
    font-weight: 400;
    color: #a3a9b5;
  }

  .ant-table-thead > tr > th {
    white-space: nowrap !important;
  }
`}</style>
      <SidebarDrawer
        open={open}
        toggleDrawer={toggleDrawer}
        singleImage={singleImage}
        userArea={userArea}
        userName={userName}
      />
    </div>
  );
};

export default ProductWiseStock;
